// src/pages/AppointmentManagement.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import nooLogo from '../assets/noo.png';
import leftArrowIcon from '../assets/left.png';

const displayFont = { fontFamily: "'Sora', ui-sans-serif, system-ui, sans-serif" };
const bodyFont = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" };
const monoFont = { fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace" };

const statusStyles = {
  Scheduled: 'bg-[#4FB3A9]/15 text-[#0F3D3E]',
  Completed: 'bg-[#0F3D3E] text-white',
  Cancelled: 'bg-red-100 text-red-700',
};

const statusDot = {
  Scheduled: 'bg-[#4FB3A9]',
  Completed: 'bg-[#0F3D3E]',
  Cancelled: 'bg-red-500',
};

const AppointmentManagement = () => {
  const mockDoctors = [
    { id: 'doc1', name: 'Dr. Ahmed Hassan' },
    { id: 'doc2', name: 'Dr. Sara Ali' },
    { id: 'doc3', name: 'Dr. Omar Mahmoud' },
  ];

  const mockPatients = [
    { id: 'pat001', name: 'Nour Ali' },
    { id: 'pat002', name: 'Fatma Khaled' },
    { id: 'pat003', name: 'Mohamed Tarek' },
  ];

  const [appointments, setAppointments] = useState(() => {
    const savedAppointments = localStorage.getItem('clinicAppointments');
    return savedAppointments ? JSON.parse(savedAppointments) : [
      { id: 'app1', patientId: 'pat001', patientName: 'Nour Ali', doctorId: 'doc1', doctorName: 'Dr. Ahmed Hassan', date: '2024-08-01', time: '10:00', notes: 'Follow-up on gastritis.', status: 'Scheduled' },
      { id: 'app2', patientId: 'pat002', patientName: 'Fatma Khaled', doctorId: 'doc2', doctorName: 'Dr. Sara Ali', date: '2024-08-01', time: '11:30', notes: 'New patient consultation.', status: 'Scheduled' },
      { id: 'app3', patientId: 'pat001', patientName: 'Ahmed Samy', doctorId: 'doc1', doctorName: 'Dr. Ahmed Hassan', date: '2024-08-02', time: '09:00', notes: 'Endoscopy procedure.', status: 'Scheduled' },
    ];
  });

  useEffect(() => {
    localStorage.setItem('clinicAppointments', JSON.stringify(appointments));
  }, [appointments]);

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showModal, setShowModal] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState(null);

  const [formPatientId, setFormPatientId] = useState('');
  const [formDoctorId, setFormDoctorId] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formTime, setFormTime] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formStatus, setFormStatus] = useState('Scheduled');

  const appointmentsForSelectedDate = appointments.filter(app => app.date === selectedDate);

  const handleAddAppointment = () => {
    setCurrentAppointment(null);
    setFormPatientId('');
    setFormDoctorId('');
    setFormDate(selectedDate);
    setFormTime('');
    setFormNotes('');
    setFormStatus('Scheduled');
    setShowModal(true);
  };

  const handleEditAppointment = (appointment) => {
    setCurrentAppointment(appointment);
    setFormPatientId(appointment.patientId);
    setFormDoctorId(appointment.doctorId);
    setFormDate(appointment.date);
    setFormTime(appointment.time);
    setFormNotes(appointment.notes);
    setFormStatus(appointment.status);
    setShowModal(true);
  };

  const handleSubmitAppointment = (e) => {
    e.preventDefault();
    const patient = mockPatients.find(p => p.id === formPatientId);
    const doctor = mockDoctors.find(d => d.id === formDoctorId);

    if (!patient || !doctor) {
      alert('Please select a valid patient and doctor.');
      return;
    }

    const newAppointment = {
      id: currentAppointment ? currentAppointment.id : `app${Date.now()}`,
      patientId: formPatientId,
      patientName: patient.name,
      doctorId: formDoctorId,
      doctorName: doctor.name,
      date: formDate,
      time: formTime,
      notes: formNotes,
      status: formStatus,
    };

    if (currentAppointment) {
      setAppointments(appointments.map(app => app.id === newAppointment.id ? newAppointment : app));
    } else {
      setAppointments([...appointments, newAppointment]);
    }
    setShowModal(false);
  };

  const handleDeleteAppointment = (id) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      setAppointments(appointments.filter(app => app.id !== id));
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" style={bodyFont}>
      {/* Header */}
      <div className="bg-white border-b border-[#E4E1D8]">
        <div className="container mx-auto px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/home">
              <img src={nooLogo} alt="Drogastro" className="w-12 h-12 object-contain" />
            </Link>
            <div>
              <p className="text-[11px] tracking-wider text-[#0F3D3E]/50" style={monoFont}>SCHEDULING</p>
              <h1 className="text-xl md:text-2xl font-bold text-[#0F3D3E]" style={displayFont}>Appointment Management</h1>
            </div>
          </div>
          <Link
            to="/home"
            className="flex items-center gap-2 bg-[#FAF9F6] hover:bg-[#E4E1D8] text-[#0F3D3E] font-semibold py-2 px-4 rounded-md border border-[#E4E1D8] transition-colors duration-200 text-sm"
          >
            <img src={leftArrowIcon} alt="Back" className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-6 md:py-8">
        <div className="bg-white rounded-xl border border-[#E4E1D8] shadow-sm p-5 md:p-8">
          <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center mb-6 gap-4">
            <div className="flex items-center gap-3 w-full md:w-auto">
              <label htmlFor="appointment-date" className="text-xs font-semibold text-[#0F3D3E]/70 whitespace-nowrap" style={monoFont}>DATE</label>
              <input
                type="date"
                id="appointment-date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="p-2 border border-[#E4E1D8] rounded-md w-full md:w-auto focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] focus:border-transparent transition-shadow"
              />
            </div>
            <button
              onClick={handleAddAppointment}
              className="bg-[#0F3D3E] hover:bg-[#0F3D3E]/90 text-white font-semibold py-2.5 px-5 rounded-md shadow-sm transition-colors duration-200 w-full md:w-auto"
            >
              + Add New Appointment
            </button>
          </div>

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-bold text-[#0F3D3E]" style={displayFont}>
              Appointments for {selectedDate}
            </h2>
            <span className="text-xs font-semibold text-[#0F3D3E]/50 bg-[#FAF9F6] border border-[#E4E1D8] rounded-full px-3 py-1" style={monoFont}>
              {appointmentsForSelectedDate.length} TOTAL
            </span>
          </div>

          {appointmentsForSelectedDate.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-14 px-6 bg-[#FAF9F6] rounded-lg border border-dashed border-[#E4E1D8]">
              <p className="text-[#22262B]/60 text-base mb-1">No appointments scheduled for this date.</p>
              <button onClick={handleAddAppointment} className="text-[#4FB3A9] font-semibold text-sm hover:underline mt-1">
                Add one now
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-[#E4E1D8]">
              <table className="min-w-full bg-white">
                <thead className="bg-[#0F3D3E]/5">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Time</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Patient</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Doctor</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Notes</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Status</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {appointmentsForSelectedDate
                    .slice()
                    .sort((a, b) => a.time.localeCompare(b.time))
                    .map(app => (
                    <tr key={app.id} className="border-t border-[#E4E1D8] hover:bg-[#FAF9F6] transition-colors">
                      <td className="py-3 px-4 whitespace-nowrap text-[#22262B] font-semibold" style={monoFont}>{app.time}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-[#22262B]">{app.patientName}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-[#22262B]">{app.doctorName}</td>
                      <td className="py-3 px-4 text-[#22262B]/80 max-w-[220px] truncate">{app.notes}</td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusStyles[app.status] || 'bg-gray-200 text-gray-800'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusDot[app.status] || 'bg-gray-500'}`} />
                          {app.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <button onClick={() => handleEditAppointment(app)} className="text-[#0F3D3E] hover:text-[#4FB3A9] font-medium mr-4 transition-colors">
                          Edit
                        </button>
                        <button onClick={() => handleDeleteAppointment(app.id)} className="text-red-600 hover:text-red-800 font-medium transition-colors">
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Appointment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-[#0F3D3E]/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl border border-[#E4E1D8] shadow-xl w-full max-w-lg overflow-hidden">
            <div className="px-8 pt-7 pb-5 border-b border-[#E4E1D8]">
              <h2 className="text-2xl font-bold text-[#0F3D3E]" style={displayFont}>
                {currentAppointment ? 'Edit Appointment' : 'Add New Appointment'}
              </h2>
            </div>
            <form onSubmit={handleSubmitAppointment} className="space-y-4 px-8 py-6">
              <div>
                <label htmlFor="patient-select" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>PATIENT</label>
                <select
                  id="patient-select"
                  value={formPatientId}
                  onChange={(e) => setFormPatientId(e.target.value)}
                  className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                  required
                >
                  <option value="">Select Patient</option>
                  {mockPatients.map(patient => (
                    <option key={patient.id} value={patient.id}>{patient.name} (ID: {patient.id})</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="doctor-select" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>DOCTOR</label>
                <select
                  id="doctor-select"
                  value={formDoctorId}
                  onChange={(e) => setFormDoctorId(e.target.value)}
                  className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                  required
                >
                  <option value="">Select Doctor</option>
                  {mockDoctors.map(doctor => (
                    <option key={doctor.id} value={doctor.id}>{doctor.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="appointment-form-date" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>DATE</label>
                  <input
                    type="date"
                    id="appointment-form-date"
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="appointment-form-time" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>TIME</label>
                  <input
                    type="time"
                    id="appointment-form-time"
                    value={formTime}
                    onChange={(e) => setFormTime(e.target.value)}
                    className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="appointment-notes" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>NOTES</label>
                <textarea
                  id="appointment-notes"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  className="w-full p-2.5 border border-[#E4E1D8] rounded-md h-24 resize-none focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                ></textarea>
              </div>
              <div>
                <label htmlFor="appointment-status" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>STATUS</label>
                <select
                  id="appointment-status"
                  value={formStatus}
                  onChange={(e) => setFormStatus(e.target.value)}
                  className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                  required
                >
                  <option value="Scheduled">Scheduled</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="bg-[#FAF9F6] hover:bg-[#E4E1D8] text-[#22262B] font-semibold py-2.5 px-5 rounded-md border border-[#E4E1D8] transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-[#0F3D3E] hover:bg-[#0F3D3E]/90 text-white font-semibold py-2.5 px-5 rounded-md shadow-sm transition-colors duration-200"
                >
                  {currentAppointment ? 'Save Changes' : 'Add Appointment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentManagement;
